import { DELTA_KEYS, type Deltas, type HoldingRow, type PnL } from '../../shared/types';

// Collapse rows that track the same underlying index into one synthetic row.
// Rows without a merge_group, or alone in their group, pass through unchanged.
// Result is sorted by value descending (the table's default order).
export function mergeSameIndex(rows: HoldingRow[]): HoldingRow[] {
  const groups = new Map<string, HoldingRow[]>();
  const out: HoldingRow[] = [];
  for (const r of rows) {
    const key = r.merge_group;
    if (!key) {
      out.push(r);
      continue;
    }
    const arr = groups.get(key);
    if (arr) arr.push(r);
    else groups.set(key, [r]);
  }
  for (const [key, members] of groups) {
    if (members.length === 1) out.push(members[0]!);
    else out.push(mergeGroup(key, members));
  }
  return out.sort((a, b) => (b.value_usd ?? 0) - (a.value_usd ?? 0));
}

function mergeGroup(key: string, members: HoldingRow[]): HoldingRow {
  let value_usd = 0;
  let value_eur = 0;
  let share_in_bucket = 0;
  let share_in_portfolio = 0;
  let riskWeighted = 0;
  const tickers: string[] = [];
  for (const m of members) {
    tickers.push(m.symbol);
    value_usd += m.value_usd ?? 0;
    value_eur += m.value_eur ?? 0;
    share_in_bucket += m.share_in_bucket;
    share_in_portfolio += m.share_in_portfolio;
    riskWeighted += (m.value_usd ?? 0) * m.risk;
  }
  return {
    symbol: key,
    name: `${members.length} holdings`,
    bucket_id: members[0]!.bucket_id,
    provider: undefined,
    provider_symbol: null,
    asset_class: members[0]!.asset_class,
    risk: value_usd > 0 ? riskWeighted / value_usd : members[0]!.risk,
    quantity: 0,
    avg_price: null,
    ccy: 'USD',
    price_usd: null,
    value_usd,
    value_eur,
    share_in_bucket,
    share_in_portfolio,
    deltas: mergeDeltas(members),
    pnl: mergePnl(members),
    merge_group: key,
    members: tickers,
  };
}

function mergeDeltas(members: HoldingRow[]): Deltas {
  const out = {} as Deltas;
  for (const w of DELTA_KEYS) {
    let abs_usd = 0;
    let abs_eur = 0;
    let prev_base = 0;
    for (const m of members) {
      abs_usd += m.deltas[w].abs_usd;
      abs_eur += m.deltas[w].abs_eur;
      prev_base += (m.value_usd ?? 0) - m.deltas[w].abs_usd;
    }
    out[w] = { abs_usd, abs_eur, pct: prev_base > 0 ? abs_usd / prev_base : 0 };
  }
  return out;
}

function mergePnl(members: HoldingRow[]): PnL | null {
  let cost_basis_usd = 0;
  let abs_usd = 0;
  let abs_eur = 0;
  let has = false;
  for (const m of members) {
    if (m.pnl == null) continue;
    has = true;
    cost_basis_usd += m.pnl.cost_basis_usd;
    abs_usd += m.pnl.abs_usd;
    abs_eur += m.pnl.abs_eur;
  }
  if (!has) return null;
  return {
    cost_basis_usd,
    abs_usd,
    abs_eur,
    pct: cost_basis_usd > 0 ? abs_usd / cost_basis_usd : 0,
    coverage: 1,
  };
}
